import {Paper, Stack} from "@mui/material";
import Grid from "@mui/material/Grid";
import {useSearch} from "@tanstack/react-router";
import moment from "moment";
import {useCallback, useEffect, useState} from "react";
import GlenSnackBar from "@/components/GlenSnackBar.tsx";
import {LoggedOutHeader} from "@/components/Navigation/LoggedOutHeader.tsx";
import {useLoggedOutFee} from "@/components/Providers/LoggedOutFeeProvider.tsx";
import useFMLogin from "@/hooks/useFMLogin.ts";
import SignInForm, {type SignInFormSchemaType} from "../components/Forms/SignInForm";
import {type JWTUser, useAuthenticateUser} from "../components/Providers/JWTProvider";

export const signInError = "Invalid Student ID or Birth Date";
export const signInUnreachableError = "Could not reach the server. Please try again.";

const allowedRoutes = ["/fees", "/cart", "/programs", "/my-registrations", "/instructions"];

type Props = {
    setUser?: (user: JWTUser | null) => void;
};

const SignIn = ({setUser}: Props) => {
    const search = useSearch({strict: false});
    const authenticateUser = useAuthenticateUser();
    const [alertMessage, setAlertMessage] = useState<string | null>(null);
    const {setFeeId} = useLoggedOutFee();
    useFMLogin();

    useEffect(() => {
        if ("feeId" in search && typeof search.feeId === "string") {
            setFeeId(search.feeId);
        }
    }, [search]);

    const handleSignInSubmit = useCallback(
        async (values: SignInFormSchemaType) => {
            const dob = moment(values.dob, "MM/DD/YYYY", true);
            let user: JWTUser | null;

            try {
                user = await authenticateUser(values.studentId, dob.format("YYYY-MM-DD"));
            } catch (e) {
                console.error("error signing in", e);
                setAlertMessage(signInUnreachableError);
                return false;
            }

            setUser && setUser(user);

            if (!user) {
                setAlertMessage(signInError);
                return false;
            }

            if (!allowedRoutes.includes(window.location.pathname)) {
                window.location.href = "/programs";
                window.location.hash = "#";
                return true;
            }

            window.location.hash = "#";
            return false;
        },
        [authenticateUser, setUser],
    );

    const handleClearAlert = useCallback(async () => {
        setAlertMessage(null);
    }, []);

    return (
        <>
            <LoggedOutHeader mode={"instructor"} />
            <Grid container justifyContent={"center"}>
                <Stack sx={{justifyContent: "center", alignItems: "center", p: 5}}>
                    <Stack sx={{maxWidth: "500px"}}>
                        <GlenSnackBar
                            onClose={handleClearAlert}
                            show={alertMessage !== null}
                            severity={"error"}
                            message={alertMessage}
                        />
                        <Paper
                            elevation={3}
                            sx={{
                                width: "100%",
                                p: 2,
                            }}
                        >
                            <SignInForm handleSignInSubmit={handleSignInSubmit} />
                        </Paper>
                    </Stack>
                </Stack>
            </Grid>
        </>
    );
};

export default SignIn;
